import { useState, useEffect } from 'react';
import './PaywallModal.css';
import { getEnvironment, isWeChat, isMobile } from '../../../utils/wechatDetector';
import { optimizeImageUrl, IMAGE_SIZES } from '../../../utils/imageOptimization';
import { trackPaywallShown, trackPaywallDismissed, trackPaywallCtaClicked } from '../../../utils/posthogEvents';

// Store configuration
const STORE_LINKS = {
  web: 'https://store.weixin.qq.com/shop/b/3nxBgQaGcBmUcIa?entrance_id=h5',
  poster: 'https://videos.littlelambenglish.com/店铺海报.jpg'
};

function PaywallModal({ isOpen, onClose, courseCode = 'phonics' }) {
  const [environment, setEnvironment] = useState('desktop');

  useEffect(() => {
    if (isOpen) {
      const env = getEnvironment();
      setEnvironment(env);
      trackPaywallShown({ courseCode, environment: env });
    }
  }, [isOpen, courseCode]);

  if (!isOpen) return null;

  const handleDismiss = () => {
    trackPaywallDismissed({ courseCode });
    onClose();
  };

  const handlePurchaseInWeChat = () => {
    trackPaywallCtaClicked({ courseCode, environment });
    // Delay navigation slightly so PostHog can flush the event over the network.
    // Otherwise the synchronous location.href assignment can interrupt the
    // in-flight capture request before it reaches the server.
    setTimeout(() => {
      window.location.href = STORE_LINKS.web;
    }, 150);
  };

  const products = {
    phonics: {
      name: '自然拼读课程',
      bootcampName: '自然拼读训练营',
      sku: 'phonics_lifetime',
      price: '¥199',
      description: '终身访问自然拼读课程'
    },
    southstreet: {
      name: '南街学校课程',
      bootcampName: '南街学校训练营',
      sku: 'southstreet_lifetime',
      price: '¥199',
      description: '终身访问南街学校课程'
    }
  };

  const product = products[courseCode] || products.phonics;

  // Render different UI based on environment
  const renderContent = () => {
    // Case A: In WeChat (Desktop or Mobile) - Direct purchase button
    if (environment === 'wechat-mobile' || environment === 'wechat-desktop') {
      return (
        <div className="paywall-body">
          <div className="paywall-icon">🔒</div>
          <p className="paywall-message">
            您需要购买 <strong>{product.name}</strong> 才能访问此内容
          </p>

          <div className="paywall-product-card">
            <div className="paywall-product-header">
              <h3>{product.name}</h3>
              <div className="paywall-price">{product.price}</div>
            </div>
            <p className="paywall-product-desc">{product.description}</p>

            <button
              className="paywall-btn-wechat"
              onClick={handlePurchaseInWeChat}
            >
              <span className="paywall-wechat-icon">🛒</span>
              前往微信小店购买
            </button>
            <p className="paywall-hint">将在微信内打开小店页面</p>
          </div>

          <div className="paywall-info">
            <p>购买后可立即访问所有课程内容</p>
          </div>
        </div>
      );
    }

    // Case B: Mobile (Not WeChat) - Show poster only
    if (environment === 'mobile-non-wechat') {
      return (
        <div className="paywall-body paywall-body-mobile">
          <div className="paywall-poster-container">
            <img
              src={optimizeImageUrl(STORE_LINKS.poster, { width: IMAGE_SIZES.POSTER })}
              alt="微信小店二维码"
              className="paywall-poster"
            />
          </div>
        </div>
      );
    }

    // Case C: Desktop - Show poster only
    return (
      <div className="paywall-body paywall-body-desktop">
        <div className="paywall-poster-container">
          <img
            src={optimizeImageUrl(STORE_LINKS.poster, { width: IMAGE_SIZES.POSTER })}
            alt="微信小店二维码"
            className="paywall-poster"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="paywall-overlay" onClick={handleDismiss}>
      <div className="paywall-content" onClick={(e) => e.stopPropagation()}>
        <div className="paywall-header">
          <h2>报名{product.bootcampName}</h2>
          <button className="paywall-close" onClick={handleDismiss}>
            ×
          </button>
        </div>

        {renderContent()}

        <div className="paywall-footer">
          <button className="paywall-btn-cancel" onClick={handleDismiss}>
            稍后再说
          </button>
        </div>

        {/* Debug info (remove in production) */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{
            padding: '10px',
            background: '#f0f0f0',
            fontSize: '10px',
            marginTop: '10px',
            borderRadius: '4px'
          }}>
            <strong>Debug:</strong> Environment = {environment} |
            WeChat = {isWeChat() ? 'Yes' : 'No'} |
            Mobile = {isMobile() ? 'Yes' : 'No'}
          </div>
        )}
      </div>
    </div>
  );
}

export default PaywallModal;
